import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { advisorHouseholdUri } from 'Consts/advisorUris';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { navigateAndClose } from 'Components/AdvisorApp/Header/ClientSearchBar/BetaSearchBar/ResultsPopper/ResultList/ResultComponent/Client/utils';
import {
  getSearchResultDOMId,
  useSearchBarDispatch, useSearchBarState,
} from '../../../../SearchBarContext';
import HouseholdIndicator from './HouseholdIndicator';
import ClientAvatar from './ClientAvatar';
import TextLine from './TextLine';
import NavigationItems from './NavigationItems';

const styles = (theme) => ({
  root: {
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.radius.xs,
    padding: theme.spacing(1),
    '&:hover': {
      cursor: 'pointer',
    },
  },
  rootSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  rootNoBorderBottom: {
    borderBottom: 'none',
  },
  avatar: {
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  contentTop: {
    display: 'flex',
  },
  titleAndSubtitleBlock: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {},
  subtitle: {},
  householdIndicatorBlock: {},
  householdIndicator: {
    marginTop: theme.spacing(0.5),
    flexShrink: 0,
  },
  navigationItems: {
    marginTop: theme.spacing(1),
  },
});

const Client = ({
  className,
  classes,
  hideBorder,
  selectedDOMId,
  searchResultResource,
}) => {
  const eventTracking = useEventTrackingLogEvent();
  const searchBarDispatch = useSearchBarDispatch();
  const searchBarState = useSearchBarState();
  const domId = getSearchResultDOMId(searchResultResource);
  const navigateOnClick = navigateAndClose(eventTracking, searchBarState, searchBarDispatch);

  return (
    <Box
      className={classNames(classes.root, className, {
        [classes.rootNoBorderBottom]: hideBorder,
        [classes.rootSelected]: selectedDOMId === domId,
      })}
      onMouseEnter={() => {
        searchBarDispatch({ type: 'setSelectionDOMId', selectionDOMId: domId });
      }}
      onMouseLeave={() => {
        searchBarDispatch({ type: 'setSelectionDOMId', selectionDOMId: null });
      }}
      id={getSearchResultDOMId(searchResultResource)}
      onClick={() => {
        return navigateOnClick(advisorHouseholdUri(searchResultResource.attr('householdId')), true);
      }}
      test={'global-search-client-result'}
    >
      <ClientAvatar
        className={classes.avatar}
        searchResultResource={searchResultResource}
      />
      <div className={classes.content}>
        <div className={classes.contentTop}>
          <div className={classes.titleAndSubtitleBlock}>
            <TextLine
              className={classes.title}
              searchResultResource={searchResultResource}
              attrName={'title'}
              matchesOn={[{ matchOn: 'name' }]}
              TypographyProps={{ color: 'black', variant: 'h5' }}
            />
            <TextLine
              className={classes.subtitle}
              searchResultResource={searchResultResource}
              attrName={'subtitle'}
              matchesOn={[
                { matchOn: 'phone', exclude: '#' },
                { matchOn: 'email' },
              ]}
              TypographyProps={{
                variant: 'body1',
                weight: 'book',
                color: 'primaryGray',
              }}
            />
          </div>
          <div className={classes.householdIndicatorBlock}>
            <HouseholdIndicator
              className={classes.householdIndicator}
              searchResultResource={searchResultResource}
            />
          </div>
        </div>
        <NavigationItems
          className={classes.navigationItems}
          searchResultResource={searchResultResource}
        />
      </div>
    </Box>
  );
};

Client.defaultProps = {
  className: '',
  selectedDOMId: null,
};

Client.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  searchResultResource: PropTypes.object.isRequired,
  hideBorder: PropTypes.bool.isRequired,
  selectedDOMId: PropTypes.string,
};

export default withStyles(styles)(Client);
